import { useRef, useEffect, useState } from "react";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/AppSidebar";
import { HeaderOptionsPopover } from "@/components/HeaderOptionsPopover";
import { Footer } from "@/components/Footer";
import { ChatMessage } from "@/components/ChatMessage";
import { ChatInput } from "@/components/ChatInput";
import { ResumeActions } from "@/components/ResumeActions";
import { ProgressTracker } from "@/components/ProgressTracker";
import { useChat } from "@/hooks/useChat";
import { useProgressTracking } from "@/hooks/useProgressTracking";
import { useTour } from "@/hooks/useTour";
import { GuidedTour } from "@/components/GuidedTour";
import { Button } from "@/components/ui/button";
import { Download, FileText, Upload, ChevronDown, Paperclip } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { toast as sonnerToast } from "sonner";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { PersonalInfoForm } from "@/components/forms/PersonalInfoForm";
import { WorkExperienceForm } from "@/components/forms/WorkExperienceForm";
import { EducationSkillsForm } from "@/components/forms/EducationSkillsForm";
import { soundManager } from "@/utils/sounds";
import { useTranslation } from "react-i18next";
import { TemplateGallery } from "@/components/TemplateGallery";
import { JobDescriptionInput } from "@/components/JobDescriptionInput";
import { ATSScorePreview } from "@/components/ATSScorePreview";
import { MissingInfoForm } from "@/components/MissingInfoForm";
import { MissingInfoActions } from "@/components/MissingInfoActions";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { AutoTranslate } from "@/lib/autoTranslate.tsx"
import { pickResumeFromDrive } from "@/lib/googleDrivePicker";
import { DriveSearchCard } from "@/components/DriveSearchCard";
import { GoogleDriveIcon } from "@/components/GoogleDriveIcon";
import { recommendTemplate } from "@/lib/templateRecommender";
import { normalizeParsedResumeData, validateParsedResumeData } from "@/utils/validation";
import { useOnlineStatus } from "@/hooks/useOnlineStatus";
import { SEO } from "@/components/SEO";

const MAX_RESUME_UPLOAD_BYTES = 5 * 1024 * 1024;

async function getEdgeFunctionErrorMessage(error: any, fallback = "Failed to upload resume") {
  const response = error?.context;

  if (response && typeof response.json === 'function') {
    try {
      const payload = await response.clone().json();
      return payload?.details || payload?.error || payload?.message || error?.message || fallback;
    } catch {
      try {
        const text = await response.clone().text();
        if (text) return text;
      } catch {
        // Use the default message below.
      }
    }
  }

  return error?.message || fallback;
}

function hasEitherUploadSignal(text: string, compact: string, textPattern: RegExp, compactPattern?: RegExp) {
  return textPattern.test(text) || Boolean(compactPattern?.test(compact));
}

function getResumeUploadEvidence(text: string) {
  const normalized = text.toLowerCase().replace(/[_-]+/g, ' ');
  const compact = normalized.replace(/[^a-z0-9@.+]/g, '');

  const candidateContactSection =
    /[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}/i.test(text) ||
    /(?:\b(?:phone|mobile|tel|telephone|contact)\b[:\s-]*\+?\d[\d\s().-]{7,}\d)|(?:\+\d{1,3}[\s().-]?\d[\d\s().-]{7,}\d)/i.test(text) ||
    hasEitherUploadSignal(text, compact, /\blinkedin\.com\/[^\s,;]+/i, /linkedincom/);

  const summarySection = hasEitherUploadSignal(
    text,
    compact,
    /\b(professional summary|career summary|career profile|personal profile|professional profile|profile|summary|objective|personal statement|about me)\b/i,
    /(professionalsummary|careersummary|careerprofile|personalprofile|professionalprofile|summary|objective|personalstatement|aboutme)/,
  );
  const workExperienceSection = hasEitherUploadSignal(
    text,
    compact,
    /\b(professional experience|work experience|employment history|career history|work history|employment experience)\b/i,
    /(professionalexperience|workexperience|employmenthistory|careerhistory|workhistory|employmentexperience)/,
  );
  const dateRangeSignal = /\b(?:(?:jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|may|jun(?:e)?|jul(?:y)?|aug(?:ust)?|sep(?:t(?:ember)?)?|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?)\.?\s*)?(?:19|20)\d{2}\s*(?:-|\u2013|\u2014|to|through)\s*(?:present|current|now|(?:(?:jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|may|jun(?:e)?|jul(?:y)?|aug(?:ust)?|sep(?:t(?:ember)?)?|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?)\.?\s*)?(?:19|20)\d{2})\b/i.test(text);
  const roleSignal = /\b(developer|engineer|manager|director|officer|assistant|coordinator|specialist|analyst|consultant|intern|teacher|nurse|accountant|designer|administrator|lead|supervisor|executive|associate|psychotherapist|therapist|wordpress|front\s*end|frontend|back\s*end|backend|full\s*stack|marketing|sales|human resources|hr|project manager|product manager|program manager|data analyst|software|customer service|operations)\b/i.test(text);
  const roleWithDatesSection = dateRangeSignal && roleSignal;
  const educationSection =
    hasEitherUploadSignal(text, compact, /\b(education|academic background|academic history|qualifications?)\b/i, /(education|academicbackground|academichistory|qualifications)/) ||
    hasEitherUploadSignal(text, compact, /\b(university|college|school|institute|academy|degree|bachelor|master|mba|msc|bsc|ba\b|phd|doctorate|diploma|certificate|certifications?)\b/i, /(university|college|school|institute|academy|degree|bachelor|master|doctorate|diploma|certificate|certification)/);
  const skillsSection = hasEitherUploadSignal(
    text,
    compact,
    /\b(skills|technical skills|core competencies|key skills|professional skills|tools|technologies)\b/i,
    /(technicalskills|corecompetencies|keyskills|professionalskills|skills|technologies)/,
  );
  const skillDenseSection = [
    /\b(javascript|typescript|react|vue|angular|node|python|java|html|css|sass|tailwind|wordpress|figma|canva|excel|microsoft office|google workspace|slack|trello|zoom|crm|hris|ats|social media|content creation|project management|data analysis|customer service)\b/i.test(text),
    /\b(communication|leadership|team collaboration|problem solving|time management|research|reporting|administrative support|client communication|calendar management)\b/i.test(text),
    hasEitherUploadSignal(text, compact, /\b(tools|systems|competencies|technologies|platforms)\b/i, /(tools|systems|competencies|technologies|platforms)/),
  ].filter(Boolean).length >= 2;
  const professionalNarrativeSection =
    /\b(?:\d+\+?\s+years?|over\s+\d+\s+years?)\s+of\s+(?:progressive\s+)?experience\b/i.test(text) ||
    (roleSignal && /\b(experience|experienced|supporting|managed|led|created|designed|coordinated|reviewed|maintained|developed|implemented|delivered)\b/i.test(text));

  const resumeEvidenceGroups = [
    candidateContactSection,
    workExperienceSection,
    roleWithDatesSection,
    educationSection,
    skillsSection || skillDenseSection,
    summarySection,
    professionalNarrativeSection,
  ].filter(Boolean).length;

  const rejectPatterns = [
    /\b(resume giving feedback|giving feedback|feedback tips|resume tips|resume review|review the resume|how to write (?:a )?resume|how to build (?:a )?resume|resume checklist)\b/i.test(text) || /(resumegivingfeedback|givingfeedback|feedbacktips|resumetips|resumereview|reviewtheresume|howtowriteresume|howtobuildresume|resumechecklist)/.test(compact),
    /\b(resume template|cv template|template|placeholder|lorem ipsum|replace this text|insert your)\b/i.test(text) || /(resumetemplate|cvtemplate|placeholder|loremipsum|replacethistext|insertyour)/.test(compact),
    /\b(worksheet|homework|fill in the blank|fill-in-the-blank|exercise|classroom|student handout|rubric|assignment|math problems?)\b/i.test(text) || /(worksheet|homework|fillintheblank|exercise|classroom|studenthandout|rubric|assignment|mathproblem)/.test(compact),
    /\b(article|blog post|news article|research paper|opinion piece|references cited)\b/i.test(text) || /(blogpost|newsarticle|researchpaper|opinionpiece|referencescited)/.test(compact),
    /\b(job description|role description|responsibilities include|ideal candidate|requirements include|apply now|equal opportunity employer)\b/i.test(text) || /(jobdescription|roledescription|responsibilitiesinclude|idealcandidate|requirementsinclude|applynow|equalopportunityemployer)/.test(compact),
    /\b(cover letter|dear hiring manager|dear recruiter|i am writing to apply)\b/i.test(text) || /(coverletter|dearhiringmanager|dearrecruiter|iamwritingtoapply)/.test(compact),
    /\b(transcript|academic record|grade report|course code|credit hours?|gpa summary)\b/i.test(text) || /(transcript|academicrecord|gradereport|coursecode|credithour|gpasummary)/.test(compact),
    /\b(business proposal|marketing proposal|proposal overview|scope of work|statement of work)\b/i.test(text) || /(businessproposal|marketingproposal|proposaloverview|scopeofwork|statementofwork)/.test(compact),
    /\b(legal pleading|contract|agreement|whereas|plaintiff|defendant|terms and conditions)\b/i.test(text) || /(legalpleading|contract|agreement|plaintiff|defendant|termsandconditions)/.test(compact),
    /\b(lesson plan|learning objectives?|teacher guide|class activity)\b/i.test(text) || /(lessonplan|learningobjective|teacherguide|classactivity)/.test(compact),
    /\b(technical manual|user manual|product specifications?|installation guide|troubleshooting guide)\b/i.test(text) || /(technicalmanual|usermanual|productspecification|installationguide|troubleshootingguide)/.test(compact),
    /\b(invoice|receipt|purchase order|amount due|subtotal|billing address|payment terms)\b/i.test(text) || /(invoice|receipt|purchaseorder|amountdue|subtotal|billingaddress|paymentterms)/.test(compact),
    /\b(thank[-\s]?you letter|thank you for the opportunity|thank you for your time|reference letter|letter of recommendation|to whom it may concern)\b/i.test(text) || /(thankyouletter|thankyoufortheopportunity|thankyouforyourtime|referenceletter|letterofrecommendation|towhomitmayconcern)/.test(compact),
  ];

  const strongCandidateResume = candidateContactSection && (roleWithDatesSection || professionalNarrativeSection) && resumeEvidenceGroups >= 3;
  const looksLikeRejectedNonResume = rejectPatterns.some(Boolean) && !strongCandidateResume;
  const looksLikeResume = resumeEvidenceGroups >= 2 && (candidateContactSection || roleWithDatesSection || professionalNarrativeSection) && !looksLikeRejectedNonResume;

  return { looksLikeResume, looksLikeRejectedNonResume, resumeEvidenceGroups };
}

function hasResumeUploadEvidence(text: string) {
  return getResumeUploadEvidence(text).looksLikeResume;
}

const Index = () => {
  const [userProfile, setUserProfile] = useState<any>(null);
  const { user } = useAuth();
  const isOnline = useOnlineStatus();
  const { toast } = useToast();
  const { t, i18n } = useTranslation();
  const [JobTitle, setJobTitle] = useState<string>("");

  // Fetch profile first so we can pass userName to useChat
  useEffect(() => {
    const fetchProfile = async () => {
      if (user) {
        if (!isOnline) return;

        const { data } = await supabase
          .from('profiles')
          .select('subscription_tier, full_name, phone_number, country_code, job_title, years_experience')
          .eq('user_id', user.id)
          .single();

        if (data) {
          setUserProfile(data);
        }
      }
    };

    fetchProfile();
  }, [isOnline, user]);

  const userName = userProfile?.full_name || undefined;

  const { messages, isLoading, processUserMessage, generateResume, editMessage, goBackToMessage, refreshChat, resumeData, currentStep, isConversationComplete, showFormType, setShowFormType, handleFormSubmit, waitingForChoice, selectOption, handleResumeUpload, showTemplateSelection, selectedTemplateId, handleTemplateSelect, isUploadedResume, uploadedResumeData, showJobDescriptionInput, showATSPreview, atsAnalysis, analyzingJD, handleJobDescriptionSubmit, handleSkipJobDescription, handleApplyImprovements, handleRejectImprovements, addLoadingMessage, removeLoadingMessage, missingInfoFields, optimizeResumeAndShowTemplate, handleMissingInfoSubmit, addAIMessage, reopenUploadChoice, recommendedTemplate, setRecommendedTemplate } = useChat(userName);
  const { isActive, currentStep: tourCurrentStep, tourSteps, hasSeenTour, startTour, nextStep, cancelTour } = useTour();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [editingMessageId, setEditingMessageId] = useState<string | null>(null);
  const [resumeDownloaded, setResumeDownloaded] = useState(false);
  const [showMissingInfoForm, setShowMissingInfoForm] = useState(false);
  const [driveSearchOpen, setDriveSearchOpen] = useState(false);

  // Progress tracking
  const progress = useProgressTracking({ messages, resumeData });
  const resumeBuilder = AutoTranslate(`Resume Builder`);
  const tourGuide = AutoTranslate(`Tour Guide`);
  const toResumeBuilder = AutoTranslate(`to Resume Builder`);
  const letOurAI = AutoTranslate(`Let our AI assistant guide you through creating a professional resume that stands out.`);
  const createANew = AutoTranslate(`Create a new resume`);
  const uploadAResume = AutoTranslate(`Upload a resume`);

  const validateResumeFile = (file: File) => {
    const validTypes = ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    const imageTypes = ['image/png', 'image/jpg', 'image/jpeg', 'image/webp', 'image/bmp', 'image/tiff', 'image/heic', 'image/heif'];
    const fileName = file.name.toLowerCase();
    const isImageFile = imageTypes.includes(file.type) || /\.(png|jpe?g|webp|bmp|tiff?|heic|heif)$/i.test(fileName);

    if (file.size > MAX_RESUME_UPLOAD_BYTES) {
      toast({
        title: "Resume file is too large",
        description: "Please upload a PDF or DOCX resume/CV up to 5 MB.",
        variant: "destructive",
      });
      return false;
    }

    if (isImageFile) {
      toast({
        title: "Images are not supported",
        description: "Please upload a readable PDF or DOCX resume/CV file.",
        variant: "destructive",
      });
      return false;
    }

    if (validTypes.includes(file.type) || /\.(pdf|docx)$/i.test(fileName)) return true;

    toast({
      title: "Invalid file type",
      description: "Please upload a PDF or DOCX file.",
      variant: "destructive",
    });
    return false;
  };

  // Start tour if user hasn't seen it
  useEffect(() => {
    if (user && !hasSeenTour) {
      const timer = setTimeout(() => {
        startTour();
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [user, hasSeenTour, startTour]);

  const handleUploadResume = async (file: File) => {
    let uploadLoadingMessageId: string | null = null;

    try {
      if (!isOnline) {
        toast({
          title: "Upload unavailable offline",
          description: "Offline build mode supports manual resume forms. Upload parsing will resume when you're online.",
          variant: "destructive",
        });
        return false;
      }

      if (!validateResumeFile(file)) {
        return false;
      }

      const formData = new FormData();
      formData.append('file', file);

      toast({
        title: "Uploading resume",
        description: "Please wait while we process your file...",
      });

      uploadLoadingMessageId = addLoadingMessage("Uploading and reading your resume");

      const { data: functionData, error: functionError } = await supabase.functions.invoke('parse-resume', {
        body: formData,
      });

      if (functionError) throw functionError;

      const parsedResumeData = normalizeParsedResumeData(functionData?.parsedData ?? {});
      const parsedValidation = validateParsedResumeData(parsedResumeData);
      const parsedSkills = parsedResumeData.skills as any;
      const hasParsedSkills = (Array.isArray(parsedSkills?.technical) && parsedSkills.technical.length > 0) ||
        (Array.isArray(parsedSkills?.soft) && parsedSkills.soft.length > 0);
      const hasParsedSupplementalSections = (Array.isArray(parsedResumeData.certifications) && parsedResumeData.certifications.length > 0) ||
        (Array.isArray(parsedResumeData.languages) && parsedResumeData.languages.length > 0) ||
        (Array.isArray(parsedResumeData.awards) && parsedResumeData.awards.length > 0) ||
        (Array.isArray(parsedResumeData.projects) && parsedResumeData.projects.length > 0) ||
        (Array.isArray(parsedResumeData.publications) && parsedResumeData.publications.length > 0) ||
        (Array.isArray(parsedResumeData.references) && parsedResumeData.references.length > 0);
      const hasParsedIdentity = Boolean(parsedResumeData.fullName || parsedResumeData.email || parsedResumeData.phone || parsedResumeData.jobTitle);
      const parsedResumeSectionCount = [
        parsedValidation.hasWorkExperience,
        parsedValidation.hasEducation,
        hasParsedSkills,
        hasParsedSupplementalSections,
      ].filter(Boolean).length;
      const classification = functionData?.classification;
      const uploadEvidenceLooksLikeResume = hasResumeUploadEvidence([
        classification?.first_100_words,
        functionData?.extractedTextPreview,
      ].filter(Boolean).join(' '));
      const parsedDataLooksLikeResume = Boolean(
        parsedValidation.isValid ||
        (hasParsedIdentity && parsedResumeSectionCount >= 1) ||
        parsedResumeSectionCount >= 2
      );
      const parsedLooksLikeResume = parsedDataLooksLikeResume || uploadEvidenceLooksLikeResume;

      // Smart Resume Auto-Detect: only refuse clear non-resume files.
      const docType = String(classification?.document_type || 'document');
      const hardRejectDocTypes = new Set([
        'image',
        'blank',
        'resume_advice',
        'template',
        'worksheet',
        'article',
        'job_description',
        'cover_letter',
        'transcript',
        'certificate',
        'proposal',
        'legal_document',
        'lesson_plan',
        'manual',
        'invoice',
        'thank_you_letter',
        'reference_letter',
      ]);

      if (classification?.is_resume === false && (!parsedDataLooksLikeResume || hardRejectDocTypes.has(docType))) {
        const friendlyTypes: Record<string, string> = {
          cover_letter: 'a cover letter',
          transcript: 'a transcript',
          certificate: 'a certificate',
          invoice: 'an invoice',
          reference_letter: 'a reference letter',
          thank_you_letter: 'a thank-you letter',
          resume_advice: 'a resume advice document',
          template: 'a resume template',
          worksheet: 'a worksheet',
          job_description: 'a job description',
          proposal: 'a proposal',
          legal_document: 'a legal document',
          lesson_plan: 'a lesson plan',
          manual: 'a manual or product specification',
          image: 'an image file',
          blank: 'a blank or placeholder document',
          article: 'an article',
          document: 'a document',
          other: 'another type of document',
        };
        const friendly = friendlyTypes[docType] || `a ${docType.replace(/_/g, ' ')}`;
        sonnerToast(`That doesn't look like a resume`, {
          description: `Looks like ${friendly}. No worries - pick your resume file and we'll try again.`,
          position: "bottom-right",
          duration: 6000,
        });
        addAIMessage(`That doesn't look like a resume. It looks like ${friendly}. Please try again with a proper resume or CV file.`, 'uploadResume');
        if (uploadLoadingMessageId) {
          removeLoadingMessage(uploadLoadingMessageId);
          uploadLoadingMessageId = null;
        }
        return false;
      } else if (classification?.is_resume === false && parsedDataLooksLikeResume) {
        console.warn('Continuing upload because parsed candidate resume data was extracted despite classifier rejection:', {
          classification,
          parsedValidation,
          parsedResumeSectionCount,
        });
      }

      const uploadAcceptedByParser = Boolean(
        parsedLooksLikeResume ||
        uploadEvidenceLooksLikeResume ||
        classification?.is_resume === true
      );

      if (!uploadAcceptedByParser) {
        addAIMessage("I couldn't read enough resume details from that file. Please try again with a clearer resume or CV file.", 'uploadResume');
        if (uploadLoadingMessageId) {
          removeLoadingMessage(uploadLoadingMessageId);
          uploadLoadingMessageId = null;
        }
        return false;
      }

      if (uploadLoadingMessageId) {
        removeLoadingMessage(uploadLoadingMessageId);
        uploadLoadingMessageId = null;
      }

      const chatAcceptedUpload = await handleResumeUpload({
        ...parsedResumeData,
        _classification: classification,
        _uploadAccepted: uploadAcceptedByParser,
        _first100Words: classification?.first_100_words,
        _extractedTextPreview: functionData?.extractedTextPreview,
        _parseWarning: functionData?.warning,
      });

      if (!chatAcceptedUpload) {
        return false;
      }

      // Pre-compute a recommended template based on parsed content so the
      // gallery can highlight it when shown.
      try {
        const rec = recommendTemplate({
          jobTitle: parsedResumeData.jobTitle,
          yearsExperience: userProfile?.years_experience,
          workExperience: parsedResumeData.workExperience,
        });
        setRecommendedTemplate({ id: rec.templateId, reason: rec.reason });
      } catch (e) {
        console.warn('Template recommendation failed:', e);
      }

      toast({
        title: "Resume uploaded successfully",
        description: "Processing your resume...",
      });
      return true;
    } catch (error: any) {
      console.error('Upload error:', error);
      const errorMessage = await getEdgeFunctionErrorMessage(error);
      toast({
        title: "Upload failed",
        description: errorMessage,
        variant: "destructive",
      });
      return false;
    } finally {
      if (uploadLoadingMessageId) {
        removeLoadingMessage(uploadLoadingMessageId);
      }
    }
  };

  const handleDownloadResume = async () => {
    if (messages.length <= 1) {
      toast({
        title: "No Information Provided",
        description: "Please provide your information first before downloading your resume.",
        variant: "destructive",
      });
      return;
    }

    // Trigger AI enhancement before download
    await generateResume();
    setResumeDownloaded(true);
  };

  const handleChatInputClick = () => {
    if (resumeDownloaded) {
      toast({
        title: "To start again, click Refresh Chat",
        description: "To start again, click **Refresh Chat**",
        variant: "default",
      });
    }
  };

  const handleProvideMissingInfo = () => {
    setShowMissingInfoForm(true);
  };

  const handleContinueWithoutInfo = () => {
    optimizeResumeAndShowTemplate();
  };

  const handleSubmitMissingInfo = (info: Record<string, string>) => {
    handleMissingInfoSubmit(info);
    setShowMissingInfoForm(false);
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // When the form-based flow is about to show templates and we don't yet
  // have a recommendation (came from forms, not upload), compute one from
  // whatever we have on the user profile + collected resume data.
  useEffect(() => {
    if (showTemplateSelection && !recommendedTemplate) {
      try {
        const rec = recommendTemplate({
          jobTitle:
            (resumeData as any)?.personalInfo?.jobTitle ||
            (resumeData as any)?.personalInfo?.title ||
            userProfile?.job_title ||
            JobTitle,
          yearsExperience: userProfile?.years_experience,
          workExperience: (resumeData as any)?.workExperience,
        });
        setRecommendedTemplate({ id: rec.templateId, reason: rec.reason });
      } catch {
        /* non-fatal */
      }
    }
  }, [showTemplateSelection, recommendedTemplate, resumeData, userProfile, JobTitle, setRecommendedTemplate]);

  const handleDriveSearchSelected = (file: File) => {
    setDriveSearchOpen(false);
    if (!validateResumeFile(file)) return;
    handleUploadResume(file);
  };

  const handleDriveSearchFallback = async () => {
    if (!isOnline) {
      toast({
        title: "Google Drive unavailable offline",
        description: "Use manual resume build mode until your connection returns.",
        variant: "destructive",
      });
      return;
    }

    setDriveSearchOpen(false);
    try {
      const file = await pickResumeFromDrive();
      if (!validateResumeFile(file)) return;
      handleUploadResume(file);
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      if (message === "Picker cancelled") return;
      toast({
        title: "Google Drive upload failed",
        description: message,
        variant: "destructive",
      });
    }
  };

  // Handle file selection for upload button in choice area
  const handleChoiceFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!isOnline) {
      toast({
        title: "Upload unavailable offline",
        description: "Use Create a new resume to keep building offline.",
        variant: "destructive",
      });
      e.target.value = "";
      return;
    }

    const file = e.target.files?.[0];
    if (file) {
      if (!validateResumeFile(file)) {
        e.target.value = "";
        return;
      }
      selectOption('upload');
      handleUploadResume(file);
    }
    e.target.value = "";
  };

  const handleChoiceGoogleDrive = () => {
    if (!isOnline) {
      toast({
        title: "Google Drive unavailable offline",
        description: "Use Create a new resume to keep building offline.",
        variant: "destructive",
      });
      return;
    }

    // Use the inline smart-search card instead of opening the heavy native picker.
    selectOption('upload');
    setDriveSearchOpen(true);
  };

  const handleRetryFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!isOnline) {
      toast({
        title: "Upload unavailable offline",
        description: "Use manual resume build mode until your connection returns.",
        variant: "destructive",
      });
      e.target.value = "";
      return;
    }

    const file = e.target.files?.[0];
    if (file) {
      if (!validateResumeFile(file)) {
        e.target.value = "";
        return;
      }
      handleUploadResume(file);
    }
    e.target.value = "";
  };

  const handleRetryGoogleDrive = () => {
    if (!isOnline) {
      toast({
        title: "Google Drive unavailable offline",
        description: "Use manual resume build mode until your connection returns.",
        variant: "destructive",
      });
      return;
    }

    setDriveSearchOpen(true);
  };

  return (
    <SidebarProvider>
      <SEO
        title="AI Resume Builder Dashboard — FIND"
        description="Chat with AI to build, tailor, and export ATS-friendly resumes. Upload an existing CV or start from scratch with FIND's guided resume builder."
        path="/app"
      />
      <div className="min-h-screen flex w-full bg-gradient-main">
        <div
          className="fixed inset-0 opacity-30 pointer-events-none dark:opacity-10"
          style={{
            backgroundImage: `var(--grid-pattern)`,
          }}
        />

        <AppSidebar />

        <main className="flex-1 flex flex-col relative z-10">
          {/* Header */}
          <header className="flex items-center justify-between gap-3 p-3 border-b border-border bg-card/80 backdrop-blur-sm sm:p-4">
            <div className="flex min-w-0 items-center gap-3 sm:gap-4">
              <SidebarTrigger data-tour="sidebar-trigger" className="hover:bg-primary-light" />
              <div className="flex min-w-0 items-center gap-2">
                <FileText className="h-5 w-5 shrink-0 text-primary sm:h-6 sm:w-6" />
                <h1 className="truncate text-base font-bold text-foreground sm:text-xl">{resumeBuilder}</h1>
              </div>
            </div>
            <HeaderOptionsPopover tourGuideLabel={tourGuide} onStartTour={startTour} />
          </header>

          {/* Chat Area */}
          <div className="flex-1 flex flex-col max-w-4xl mx-auto w-full min-w-0">
            {/* Welcome Message */}
            <div className="text-center py-4 px-3 sm:py-8 sm:px-6">
              <div className="bg-card/80 backdrop-blur-sm rounded-2xl border border-card-border p-4 shadow-lg sm:p-8">
                <h2 className="text-2xl font-bold text-foreground mb-3 sm:text-3xl sm:mb-4">
                  {t('welcome')} {toResumeBuilder}
                </h2>
                <p className="text-sm text-muted-foreground sm:text-lg">
                  {letOurAI}
                </p>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto px-3 pb-4 sm:px-6" data-tour="chat-area">
              <div className="space-y-4" aria-live="polite">
                {messages.map((message, index) => (
                  <div key={message.id}>
                    <ChatMessage
                      message={message.content}
                      isUser={message.isUser}
                      timestamp={message.timestamp}
                      isLoading={message.isLoading}
                      onEdit={message.isUser ? () => {
                        setEditingMessageId(message.id);
                      } : undefined}
                      onGoBack={
                        message.isUser &&
                        (userProfile?.subscription_tier === "pro" || index === 1)
                          ? () => goBackToMessage?.(message.id)
                          : undefined
                      }
                      goBackAllowed={
                        userProfile?.subscription_tier === "pro" || index === 1
                      }
                    />

                    {message.action === 'refreshChat' && index === messages.length - 1 && (
                      <div className="mt-3 ml-11">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            refreshChat();
                            setResumeDownloaded(false);
                          }}
                        >
                          {t('chatActions.refreshChat')}
                        </Button>
                      </div>
                    )}

                    {message.action === 'uploadResume' && index === messages.length - 1 && (
                      <div className="mt-3 ml-11">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="outline"
                              size="sm"
                              className="gap-2"
                              disabled={!isOnline}
                            >
                              <Upload className="h-4 w-4" />
                              {uploadAResume}
                              <ChevronDown className="h-3 w-3 ml-1" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="start" className="w-48">
                            <DropdownMenuItem onClick={handleRetryGoogleDrive}>
                              <GoogleDriveIcon className="h-4 w-4 mr-2 shrink-0" />
                              <span>Google Drive</span>
                            </DropdownMenuItem>
                            <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                              <label htmlFor={`retry-upload-input-${message.id}`} className="cursor-pointer flex items-center gap-0 w-full">
                                <Paperclip className="h-4 w-4 mr-2" />
                                <span>From Device</span>
                                <input
                                  id={`retry-upload-input-${message.id}`}
                                  type="file"
                                  accept=".pdf,.docx"
                                  className="hidden"
                                  onChange={handleRetryFileSelect}
                                />
                              </label>
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    )}

                    {/* Show choice buttons if waiting for choice */}
                    {!message.isUser && waitingForChoice && index === messages.length - 1 && (
                      <div className="flex gap-3 mt-3 ml-11">
                        <Button
                          onClick={() => selectOption('create')}
                          variant="outline"
                          className="flex-1"
                        >
                          {createANew}
                        </Button>

                        {/* Upload button with dropdown */}
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="outline"
                              className="flex-1 gap-2"
                              disabled={!isOnline}
                            >
                              <Upload className="h-4 w-4" />
                              {AutoTranslate(`Upload a resume`)}
                              <ChevronDown className="h-3 w-3 ml-1" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-48">
                            <DropdownMenuItem onClick={handleChoiceGoogleDrive}>
                              <GoogleDriveIcon className="h-4 w-4 mr-2 shrink-0" />
                              <span>Google Drive</span>
                            </DropdownMenuItem>
                            <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                              <label htmlFor="choice-upload-input" className="cursor-pointer flex items-center gap-0 w-full">
                                <Paperclip className="h-4 w-4 mr-2" />
                                <span>From Device</span>
                                <input
                                  id="choice-upload-input"
                                  type="file"
                                  accept=".pdf,.docx"
                                  className="hidden"
                                  onChange={handleChoiceFileSelect}
                                />
                              </label>
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    )}

                    {/* Show form if this message requests it */}
                    {!message.isUser && message.formType && showFormType === message.formType && index === messages.length - 1 && (
                      <div className="mt-4">
                        {message.formType === 'personal' && (
                          <PersonalInfoForm onSubmit={(data) => {
                            handleFormSubmit(data, 'personal');
                            setJobTitle(data.jobTitle);
                          }} />
                        )}
                        {message.formType === 'work' && (
                          <WorkExperienceForm
                            onSubmit={(data) => handleFormSubmit({ workExperience: data }, 'work')}
                            language={i18n.language}
                          />
                        )}
                        {message.formType === 'education' && (
                          <EducationSkillsForm
                            onSubmit={(data) => handleFormSubmit(data, 'education')}
                            jobTitle={JobTitle}
                          />
                        )}
                      </div>
                    )}
                  </div>
                ))}

                {isLoading && (
                  <div className="flex justify-start mb-4" role="status">
                    <div className="flex gap-3">
                      <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center overflow-hidden">
                        <img
                          src="/lovable-uploads/c96cfab8-cc2c-42cf-afbf-d5cab483bb7d.png"
                          alt="AI Assistant"
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="bg-chat-ai rounded-2xl px-4 py-3">
                        <div className="flex space-x-1">
                          <div className="w-2 h-2 bg-primary rounded-full animate-bounce"></div>
                          <div className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                          <div className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {showJobDescriptionInput && (
                  <div className="mt-4">
                    <JobDescriptionInput
                      onSubmit={handleJobDescriptionSubmit}
                      onSkip={handleSkipJobDescription}
                      isLoading={analyzingJD}
                    />
                  </div>
                )}

                {showATSPreview && atsAnalysis && (
                  <div className="mt-4">
                    <ATSScorePreview
                      analysis={atsAnalysis}
                      onApplyImprovements={handleApplyImprovements}
                      onReject={handleRejectImprovements}
                    />
                  </div>
                )}

                {showTemplateSelection && (
                  <div className="mt-4">
                    <TemplateGallery
                      onSelectTemplate={handleTemplateSelect}
                      showKeepOriginal={isUploadedResume}
                      recommendedTemplateId={recommendedTemplate?.id}
                      recommendationReason={recommendedTemplate?.reason}
                    />
                  </div>
                )}

                {driveSearchOpen && (
                  <div className="mt-4">
                    <DriveSearchCard
                      onFileSelected={handleDriveSearchSelected}
                      onCancel={() => setDriveSearchOpen(false)}
                      onFallbackToFullPicker={handleDriveSearchFallback}
                    />
                  </div>
                )}

                {/* Missing Info Actions - Show after upload when info is missing */}
                {currentStep === 'resume-upload-missing-info' && missingInfoFields.length > 0 && !showMissingInfoForm && (
                  <div className="mt-4">
                    <MissingInfoActions
                      missingFields={missingInfoFields}
                      onProvideInfo={handleProvideMissingInfo}
                      onContinue={handleContinueWithoutInfo}
                      isLoading={isLoading}
                    />
                  </div>
                )}

                {/* Missing Info Form - Show when user clicks "Provide" */}
                {showMissingInfoForm && missingInfoFields.length > 0 && (
                  <div className="mt-4">
                    <MissingInfoForm
                      missingFields={missingInfoFields}
                      onProvideInfo={handleSubmitMissingInfo}
                      onContinue={() => {
                        setShowMissingInfoForm(false);
                        handleContinueWithoutInfo();
                      }}
                      isLoading={isLoading}
                    />
                  </div>
                )}

                <div ref={messagesEndRef} />
              </div>
            </div>

            {/* Progress Tracker */}
            <ProgressTracker progress={progress} />

            {/* Chat Input */}
            <div className="relative" onClick={handleChatInputClick} data-tour="chat-input">
              <ChatInput
                onSendMessage={resumeDownloaded ? () => { } : processUserMessage}
                isLoading={isLoading}
                placeholder={resumeDownloaded || currentStep === 'stopped' ? "To start again, click Refresh Chat" : "Type here..."}
                onRefreshChat={() => {
                  refreshChat();
                  setResumeDownloaded(false);
                }}
                onUploadResume={handleUploadResume}
                userProfile={userProfile}
                disabled={resumeDownloaded || currentStep === 'stopped'}
                isConversationComplete={isConversationComplete}
                onOpenDriveSearch={() => setDriveSearchOpen(true)}
              />
            </div>

            {/* Resume Actions - Show after messages */}
            {messages.length > 0 && (
              <div className="mx-6">
                <ResumeActions
                  resumeData={resumeData}
                  isConversationComplete={isConversationComplete}
                  selectedTemplateId={selectedTemplateId}
                  onSave={() => console.log('Resume saved')}
                />
              </div>
            )}
          </div>

          {/* Footer */}
          <Footer />
        </main>
      </div>

      {isActive && (
        <GuidedTour
          step={tourSteps[tourCurrentStep]}
          totalSteps={tourSteps.length}
          onNext={nextStep}
          onCancel={cancelTour}
        />
      )}
    </SidebarProvider>
  );
};

export default Index;
